import { useDroppable } from '@dnd-kit/core';
import { useAtomValue } from 'jotai';
import React from 'react';
import clsx from 'clsx';
import { useSettingsCanvas } from '../../../WorkflowTabs/useSettingsCanvas';
import styles from './DropZone.module.scss';
import { getDropZoneLabel, getDropZoneBackgroundColor } from './utils';
import { hideDropZoneIconsAtom, hideDropZoneTextAtom } from '../../../atoms';

export interface DropZoneProperties {
  isEmpty?: boolean;
  isFailback?: boolean;
  isGlobalFailback?: boolean;
  isNested?: boolean;
  isNextStepGroup?: boolean;
  isParallelStep?: boolean;
  isPreviousStepGroup?: boolean;
  parentWorkflowStepGroupId?: number | null;
  style?: React.CSSProperties;
  workflowStepGroupId?: number | null;
  workflowStepId?: number | null;
}

const DropZone: React.FC<DropZoneProperties> = ({
  workflowStepGroupId = 0,
  workflowStepId = 0,
  isNested = false,
  isNextStepGroup = false,
  isParallelStep = false,
  isPreviousStepGroup = false,
  isFailback = false,
  isGlobalFailback = false,
  isEmpty = false,
  parentWorkflowStepGroupId = null,
  style = {},
}) => {
  const hideIcons = useAtomValue(hideDropZoneIconsAtom);
  const hideText = useAtomValue(hideDropZoneTextAtom);

  const dropZoneId = `dropzone-${workflowStepGroupId}-${workflowStepId}-${isNested}-${isFailback}-${isGlobalFailback}-${isNextStepGroup}-${isParallelStep}-${isPreviousStepGroup}-${parentWorkflowStepGroupId}`;

  const { setNodeRef, isOver } = useDroppable({
    data: {
      isFailback,
      isGlobalFailback,
      isNested,
      isNextStepGroup,
      isParallelStep,
      isPreviousStepGroup,
      parentWorkflowStepGroupId,
      workflowStepGroupId,
      workflowStepId,
    },
    id: dropZoneId,
  });
  const { disableBackgroundColor } = useSettingsCanvas();
  const labelText = getDropZoneLabel({
    disableBackgroundColor,
    hideIcons,
    hideText,
    isFailback,
    isGlobalFailback,
    isNested,
    isNextStepGroup,
    isOver,
    isParallelStep,
    isPreviousStepGroup,
  });

  const backgroundColor = getDropZoneBackgroundColor({
    isFailback,
    isGlobalFailback,
    isNested,
    isNextStepGroup,
    isOver,
    isParallelStep,
  });
  if (isEmpty) {
    return <div className={styles.empty} />;
  }
  return (
    <div
      ref={setNodeRef}
      className={clsx(styles.dashedBorderGray, styles.dropZone, {
        [styles.dropZoneOver]: isOver,
        [styles.dashedBorderRedRounded]: isFailback,
        [styles.dashedBorderGrayRounded]: isNested,
        [styles.dashedBorderRed]: isGlobalFailback,
      })}
      style={{
        backgroundColor: disableBackgroundColor ? undefined : backgroundColor,
        ...style,
      }}
    >
      <div>{labelText}</div>
    </div>
  );
};

export default DropZone;
